// Grouped, collapsible left sidebar — the app's primary navigation.
import React from 'react';
import {
  LayoutDashboard, BarChart3, Search, MapPinned, Database, Mail, Inbox,
  TrendingUp, MonitorPlay, HeartPulse, Settings as SettingsIcon, MessagesSquare,
  ChevronsLeft, ChevronsRight, X,
} from 'lucide-react';

export const NAV_GROUPS = [
  { title: null, items: [
    { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { key: 'analytics', label: 'Analytics', icon: BarChart3 },
  ] },
  {
    title: 'Acquire',
    items: [
      { key: 'scraper', label: 'Scraper', icon: Search },
      { key: 'collector', label: 'Lead Collector', icon: MapPinned },
      { key: 'records', label: 'Records', icon: Database },
    ],
  },
  {
    title: 'Engage',
    items: [
      { key: 'email', label: 'Campaigns', icon: Mail },
      { key: 'inbox', label: 'Inbox', icon: Inbox },
      { key: 'followups', label: 'Follow-ups', icon: MessagesSquare },
    ],
  },
  {
    title: 'Close',
    items: [
      { key: 'pipeline', label: 'Pipeline', icon: TrendingUp },
      { key: 'demos', label: 'Demos', icon: MonitorPlay },
    ],
  },
  {
    title: 'System',
    items: [
      { key: 'mailboxes', label: 'Mailboxes', icon: HeartPulse },
      { key: 'settings', label: 'Settings', icon: SettingsIcon },
    ],
  },
];

export const PAGE_TITLES = Object.fromEntries(
  NAV_GROUPS.flatMap((g) => g.items.map((i) => [i.key, i.label]))
);

const Sidebar = ({ active, onSelect, collapsed, onToggleCollapse, isMobile, mobileOpen, onCloseMobile }) => {
  const width = collapsed && !isMobile ? 68 : 232;

  const body = (
    <div style={{
      width, height: '100%', display: 'flex', flexDirection: 'column',
      background: 'var(--c-bg-1)', borderRight: '1px solid var(--c-border)',
      transition: 'width 0.2s ease', overflow: 'hidden',
    }}>
      {/* Brand */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 11, padding: collapsed && !isMobile ? '18px 14px' : '18px 18px',
        borderBottom: '1px solid var(--c-border)', minHeight: 64, boxSizing: 'border-box',
      }}>
        <img src="/halvecost-logo.png" alt="WNLeads" style={{ height: 32, width: 32, objectFit: 'contain', flexShrink: 0 }} />
        {(!collapsed || isMobile) && (
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 16, lineHeight: 1, whiteSpace: 'nowrap' }}>
              <span style={{ color: 'var(--c-primary)' }}>WNleads</span>
              <span style={{ color: 'var(--c-accent)' }}> PRO</span>
            </div>
            <div style={{ color: 'var(--c-text-3)', fontSize: 9, letterSpacing: '0.4px', textTransform: 'uppercase', marginTop: 3, whiteSpace: 'nowrap' }}>
              by writenow agency
            </div>
          </div>
        )}
        {isMobile && (
          <button onClick={onCloseMobile} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--c-text-2)', cursor: 'pointer', padding: 6 }}>
            <X size={18} />
          </button>
        )}
      </div>

      {/* Nav groups */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 10px' }}>
        {NAV_GROUPS.map((group, gi) => (
          <div key={gi} style={{ marginBottom: 6 }}>
            {group.title && (!collapsed || isMobile) && (
              <div style={{
                padding: '12px 10px 5px', fontSize: 10, fontWeight: 700,
                color: 'var(--c-text-3)', textTransform: 'uppercase', letterSpacing: '0.8px',
              }}>
                {group.title}
              </div>
            )}
            {group.title && collapsed && !isMobile && (
              <div style={{ borderTop: '1px solid var(--c-border)', margin: '10px 8px' }} />
            )}
            {group.items.map(({ key, label, icon: Icon }) => {
              const isActive = active === key;
              return (
                <button
                  key={key}
                  onClick={() => { onSelect(key); if (isMobile) onCloseMobile(); }}
                  title={collapsed && !isMobile ? label : undefined}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 11,
                    justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                    padding: collapsed && !isMobile ? '11px 0' : '10px 12px',
                    marginBottom: 2, borderRadius: 10,
                    border: isActive ? '1px solid var(--c-border-bright)' : '1px solid transparent',
                    background: isActive ? 'var(--c-primary-glow)' : 'transparent',
                    color: isActive ? 'var(--c-primary)' : 'var(--c-text-2)',
                    fontFamily: "'Inter', sans-serif", fontWeight: isActive ? 600 : 500,
                    fontSize: 13.5, cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.color = 'var(--c-text)'; e.currentTarget.style.background = 'var(--c-surface-hover)'; } }}
                  onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.color = 'var(--c-text-2)'; e.currentTarget.style.background = 'transparent'; } }}
                >
                  <Icon size={16} style={{ flexShrink: 0 }} />
                  {(!collapsed || isMobile) && label}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Collapse toggle */}
      {!isMobile && (
        <button
          onClick={onToggleCollapse}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: '13px 0', border: 'none', borderTop: '1px solid var(--c-border)',
            background: 'transparent', color: 'var(--c-text-3)', cursor: 'pointer',
            fontSize: 12, fontFamily: "'Inter', sans-serif", fontWeight: 500,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--c-primary)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--c-text-3)'; }}
        >
          {collapsed ? <ChevronsRight size={16} /> : <><ChevronsLeft size={16} /> Collapse</>}
        </button>
      )}
    </div>
  );

  if (isMobile) {
    if (!mobileOpen) return null;
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 250 }}>
        <div style={{ position: 'absolute', inset: 0, background: 'var(--c-overlay)' }} onClick={onCloseMobile} />
        <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, animation: 'scaleIn 0.15s ease' }}>{body}</div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 100 }}>
      {body}
    </div>
  );
};

export default Sidebar;
